import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "../api/trpc";
import { AuthContext, LoginResult } from "./authContext";
import {
  deleteTokensFromStorage,
  getRoles,
  getTokensFromStorage,
  setTokensInStorage,
  tokenIsValid,
} from "./tokenHelper";

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const loginMutation = trpc.publicUser.login.useMutation();

  const [token, setToken] = useState<string | null | undefined>(undefined);

  const updateTokens = (t: string) => {
    setTokensInStorage(t);
    setToken(t);
  };

  useEffect(() => {
    (async () => {
      const [loadedToken] = await getTokensFromStorage();
      if (loadedToken && tokenIsValid(loadedToken)) {
        setToken(loadedToken);
      } else {
        setToken(null);
      }
    })();
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      if (!token) return; // already logged out
      toast.error("Your session has expired. Please log in again.", { id: "session-expired" });
      deleteTokensFromStorage();
      setToken(null);
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, [token]);

  const providerValue = useMemo(() => {
    const getAuthToken = async (): Promise<string> => {
      if (token && tokenIsValid(token)) return token;

      // Invalid token
      setToken(null);

      return "";
    };

    const signIn = async (
      username: string,
      password: string,
    ): Promise<LoginResult> => {
      try {
        const result = await loginMutation.mutateAsync({
          email: username,
          password,
        });

        if (result.access_token) {
          updateTokens(result.access_token);
          return LoginResult.successful;
        }
      } catch (error) {
        console.error("Login failed:", error);
      }

      return LoginResult.failed;
    };

    const logout = () => {
      deleteTokensFromStorage();
      setToken(null);
    };

    return {
      isAuthenticated: !!token,
      authIsLoading: token === undefined,
      getAuthToken,
      signIn,
      logout,
      roles: getRoles(token),
    };
  }, [token]);

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
